/**
 * MedicationAISummary — pulls a clinical-pharmacology overview for the given
 * free-text medication list. Wired into the patient interview's medications
 * field on the clinician/admin side.
 *
 * Props:
 *   medicationsText  string — the textarea content (free text)
 *   patientId?       string — optional context to thread into the LLM call
 *
 * Renders an inline button → expandable panel showing:
 *   • 2-3 sentence plain-English summary
 *   • Per-medication breakdown (class / purpose / PT relevance / interactions)
 *   • Risk flags (sedation, fall risk, anticoagulation, …)
 */
import React, { useState } from 'react';
import { api } from '../lib/api';
import { Sparkles, ChevronDown, ChevronUp, Loader2, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

const FLAG_LABELS = {
  sedation: { label: 'Sedation', color: '#7A4D9A' },
  fall_risk: { label: 'Fall risk', color: '#B83A3A' },
  anticoagulation: { label: 'Anticoagulation', color: '#003D80' },
  orthostatic_hypotension: { label: 'Orthostatic hypotension', color: '#FFB000' },
  impaired_healing: { label: 'Impaired healing', color: '#5A3A00' },
  none: { label: 'No major flags', color: '#3A664D' },
};

export default function MedicationAISummary({ medicationsText, patientId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    const text = (medicationsText || '').trim();
    if (text.length < 3) {
      toast.error('Type a medication list first.');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/ai/medication-summary', {
        medications: text,
        patient_id: patientId || undefined,
      });
      setData(r.data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI summary failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2" data-testid="med-ai-root">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={generate} disabled={busy}
                className="btn-secondary text-xs inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="med-ai-generate">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Sparkles className="w-3.5 h-3.5 text-brand"/>}
          {busy ? 'Analyzing…' : 'AI medication summary'}
        </button>
        {data && (
          <button onClick={()=>setOpen(o => !o)} className="text-xs text-fg-muted hover:text-fg inline-flex items-center gap-1">
            {open ? <ChevronUp className="w-3.5 h-3.5"/> : <ChevronDown className="w-3.5 h-3.5"/>}
            {open ? 'Hide' : 'Show'}
          </button>
        )}
      </div>

      {data && open && (
        <div className="mt-3 surface border border-soft rounded-lg p-4" data-testid="med-ai-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-fg-muted font-medium">CLINICAL SUMMARY</div>
            <button onClick={()=>setOpen(false)} className="text-fg-muted hover:text-fg"><X className="w-3.5 h-3.5"/></button>
          </div>
          <p className="text-sm text-fg-secondary mb-3" data-testid="med-ai-summary">{data.summary}</p>

          {(data.flags || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="med-ai-flags">
              {data.flags.map((f, i) => {
                const m = FLAG_LABELS[f] || { label: f, color: '#7A827A' };
                return (
                  <span key={i} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"
                        style={{ background: `${m.color}1A`, color: m.color }}>
                    <AlertTriangle className="w-3 h-3"/> {m.label}
                  </span>
                );
              })}
            </div>
          )}

          {(data.medications || []).length > 0 && (
            <div className="space-y-2" data-testid="med-ai-list">
              {data.medications.map((m, i) => (
                <div key={i} className="border-l-2 border-brand/40 pl-3 py-1 text-sm" data-testid={`med-ai-item-${i}`}>
                  <div className="font-medium">{m.name} {m.class ? <span className="text-[10px] uppercase tracking-wider text-fg-muted ml-1">{m.class}</span> : null}</div>
                  {m.purpose && <div className="text-xs text-fg-secondary mt-0.5"><strong>Purpose:</strong> {m.purpose}</div>}
                  {m.pt_relevance && <div className="text-xs text-fg-secondary mt-0.5"><strong>PT relevance:</strong> {m.pt_relevance}</div>}
                  {m.interactions && <div className="text-xs text-fg-secondary mt-0.5"><strong>Interactions:</strong> {m.interactions}</div>}
                </div>
              ))}
            </div>
          )}

          <p className="text-[10px] text-fg-muted mt-3 italic">
            AI-assisted clinical summary — not prescription advice. Always confirm with the prescribing physician.
          </p>
        </div>
      )}
    </div>
  );
}
